import React, { createContext, useState, useEffect, useCallback } from 'react';
import { db } from '../firebase';
import { doc, getDoc, getDocs, collection, query, where } from 'firebase/firestore';
import { useAuth } from '../hooks/useAuth';
import { clearDashboardViewState } from '../utils/dashboardViewState';
import { clearAutismHistorySessionState } from '../utils/autismHistorySessionState';
import { clearModularPatientLocalStorage } from '../utils/modularPatientSessionState';
import { clearCompareOutputsCache } from '../utils/compareOutputsCache';
import { stripAdminAnalyticsFields } from '../utils/patientSummaryCache';

export const GlobalStateContext = createContext();

export const GlobalStateProvider = ({ children }) => {
  const [userData, setUserData] = useState(null);
  const [patientsData, setPatientsData] = useState([]);
  const [providerAccountsData, setProviderAccountsData] = useState([]);
  const [patientsLoading, setPatientsLoading] = useState(false);
  
  // Session storage cache key (versioned to allow safe future format changes)
  // Bumped to v2: data now comes from patient_summaries collection (server-side summaries)
  // instead of full patient docs with client-side summary computation.
  const PATIENTS_CACHE_STORAGE_KEY = 'td_patients_cache_v2';
  
  // Lightweight TTL cache metadata for dashboard patients
  const [patientsCacheMeta, setPatientsCacheMeta] = useState({
    lastLoadedAt: 0,
    loadedForOrg: null,
    loadedForUserId: null,
    loadedRole: null,
  });
  const PATIENTS_TTL_MS = 30 * 1000; // 30 seconds

  // Prevent duplicate background refreshes
  const inFlightPatientsFetchRef = React.useRef(null);
  
  // Get auth state from consolidated useAuth hook
  // Important: During a normal page refresh, auth starts in "loading" state.
  // We must NOT treat that as a logout (or we'd clear sessionStorage and defeat cold-load hydration).
  const { user: currentUser, claims, isAuthenticated, isLoading: authLoading } = useAuth();

  // Add a specific function to refresh provider accounts data
  const refreshProviderAccountsData = useCallback(async () => {
    try {
      if (!currentUser) return;

      // Get the current clinician data
      const clinicianDoc = await getDoc(doc(db, 'clinicians', currentUser.uid));
      if (!clinicianDoc.exists()) return;

      const clinicianData = clinicianDoc.data();

      // Staff list is intentionally shared between Org. Admin and Support users:
      // - Both roles need a full view of provider accounts within their organisation.
      // - Clinicians should not fetch the org-wide staff list from this helper.
      if (clinicianData.userType === 'Org. Admin' || clinicianData.userType === 'Support') {
        const providerAccountsQuery = query(
          collection(db, 'clinicians'),
          where('organisation', '==', clinicianData.organisation)
        );
        const providerAccountsSnapshot = await getDocs(providerAccountsQuery);
        const providerAccountsList = providerAccountsSnapshot.docs.map(doc => ({
          clinicianId: doc.id,
          ...doc.data()
        }));
        console.log(`Refreshed provider accounts list: loaded ${providerAccountsList.length} accounts`);
        setProviderAccountsData(providerAccountsList);
      } else {
        // If a non-staff role somehow calls this, clear any stale data to avoid
        // leaking a previous staff member's view into a different session.
        setProviderAccountsData([]);
      }
    } catch (error) {
      console.error("Error refreshing provider accounts data:", error);
    }
  }, [currentUser]);

  // Internal: fetch patient summaries from the patient_summaries collection.
  // This collection contains lightweight, pre-computed data maintained by Cloud Functions,
  // designed specifically for fast dashboard table rendering.
  const fetchSummariesFromServer = useCallback(async (clinicianData, clinicianUid) => {
    if (clinicianData.userType === 'Org. Admin' || clinicianData.userType === 'Support') {
      // Org Admin / Support: all org patient summaries
      const q = query(
        collection(db, 'patient_summaries'),
        where('organisation', '==', clinicianData.organisation)
      );
      const snap = await getDocs(q);
      return snap.docs.map(d => ({ patientId: d.id, ...d.data(), _summary: true }));
    }

    // Clinician: only summaries for patients this clinician is assigned to.
    // The assignedClinicianIds array on each summary enables a single efficient query
    // (no batching by patient ID needed).
    const q = query(
      collection(db, 'patient_summaries'),
      where('organisation', '==', clinicianData.organisation),
      where('assignedClinicianIds', 'array-contains', clinicianUid)
    );
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ patientId: d.id, ...d.data(), _summary: true }));
  }, []);

  // Convert a Firestore Timestamp (or Date-like value) to epoch milliseconds for JSON.
  const toMs = (val) => {
    if (!val) return null;
    try {
      if (val?.toDate) return val.toDate().getTime();           // Firestore Timestamp
      if (val?.seconds != null) return val.seconds * 1000;      // Timestamp-like {seconds, nanoseconds}
      const t = new Date(val).getTime();                        // Date string or number
      return Number.isFinite(t) ? t : null;
    } catch (_) { return null; }
  };

  // Internal: sessionStorage helpers
  // Summaries are a few KB each. Before persisting we drop the admin-analytics
  // fields the dashboard never reads (see utils/patientSummaryCache.js) so the
  // cache stays within the ~5 MB sessionStorage quota for longer and
  // special-category demographics are not kept in browser storage at rest.
  // The in-memory list keeps the full server shape. Firestore Timestamps are
  // converted for JSON serialisation.
  const savePatientsCacheToSession = useCallback((list, meta) => {
    try {
      const summaries = Array.isArray(list) ? list.map(s => ({
        ...stripAdminAnalyticsFields(s),
        createdAt: toMs(s.createdAt),
        archivedAt: toMs(s.archivedAt),
        summaryUpdatedAt: undefined,   // Strip server-only field
        _summary: true,
      })) : [];
      const payload = { patients: summaries, meta: { ...meta, summary: true } };
      const json = JSON.stringify(payload);
      sessionStorage.setItem(PATIENTS_CACHE_STORAGE_KEY, json);
      console.info(`Persisted ${summaries.length} patient summaries to sessionStorage (~${new Blob([json]).size} bytes)`);
    } catch (e) {
      // Non-fatal; storage can be unavailable (private mode, quotas)
      console.info('Skipped persisting patients cache to sessionStorage:', e?.name || e);
    }
  }, []);

  /**
   * Internal helper to build the best possible cache metadata for persisting summaries.
   *
   * Why this exists:
   * - Optimistic updates can happen before/without a fresh fetch completing.
   * - We still want sessionStorage to reflect the user's *current* view instantly (so a refresh doesn't "undo" the optimistic UI).
   * - The hydration code relies on loadedForUserId/org/role to prevent cross-user/org leakage.
   */
  const getPatientsCacheMetaForPersistence = useCallback(() => {
    // Prefer the meta from the most recent successful load.
    if (patientsCacheMeta?.loadedForUserId && patientsCacheMeta?.loadedForOrg && patientsCacheMeta?.loadedRole) {
      return patientsCacheMeta;
    }
    // Fallback: use the currently authenticated user + userData (clinician record) if available.
    if (currentUser?.uid && userData?.organisation && userData?.userType) {
      return {
        lastLoadedAt: patientsCacheMeta?.lastLoadedAt || Date.now(),
        loadedForOrg: userData.organisation,
        loadedForUserId: currentUser.uid,
        loadedRole: userData.userType,
      };
    }
    // Worst-case: return current meta as-is (may prevent hydration until a real fetch occurs)
    return patientsCacheMeta;
  }, [patientsCacheMeta, currentUser, userData]);

  /**
   * Optimistically update the in-memory patients list *and* sessionStorage summaries.
   *
   * Design decision:
   * - We want the UI to feel instant when a user archives/restores/deletes a patient.
   * - So we update the list immediately in the browser, then re-fetch from Firestore in the background to confirm truth.
   * - If the backend write fails, the caller should force-refresh to rollback.
   */
  const applyOptimisticPatientsUpdate = useCallback((updater) => {
    setPatientsData((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : prev;
      try {
        const meta = getPatientsCacheMetaForPersistence();
        savePatientsCacheToSession(next, meta);
      } catch (_) {}
      return next;
    });
  }, [getPatientsCacheMetaForPersistence, savePatientsCacheToSession]);

  const optimisticRemovePatient = useCallback((patientId) => {
    if (!patientId) return;
    applyOptimisticPatientsUpdate((prev) => prev.filter((p) => p?.patientId !== patientId));
  }, [applyOptimisticPatientsUpdate]);

  const optimisticArchivePatient = useCallback((patientId, archivePatch = {}) => {
    if (!patientId) return;
    applyOptimisticPatientsUpdate((prev) => prev.map((p) => {
      if (p?.patientId !== patientId) return p;
      return {
        ...p,
        isArchived: true,
        archiveReason: archivePatch.archiveReason ?? p.archiveReason ?? null,
        archiveNote: typeof archivePatch.archiveNote !== 'undefined' ? archivePatch.archiveNote : (p.archiveNote ?? null),
        archivedAt: archivePatch.archivedAt ?? p.archivedAt ?? Date.now(),
        archivedBy: archivePatch.archivedBy ?? p.archivedBy ?? null,
        archivedByName: archivePatch.archivedByName ?? p.archivedByName ?? null,
      };
    }));
  }, [applyOptimisticPatientsUpdate]);

  const optimisticRestorePatient = useCallback((patientId) => {
    if (!patientId) return;
    applyOptimisticPatientsUpdate((prev) => prev.map((p) => {
      if (p?.patientId !== patientId) return p;
      return {
        ...p,
        isArchived: false,
        archiveReason: null,
        archiveNote: null,
        archivedAt: null,
        archivedBy: null,
        archivedByName: null,
      };
    }));
  }, [applyOptimisticPatientsUpdate]);

  /**
   * Optimistically add a newly-created patient to the in-memory list.
   *
   * Called immediately after setupNewPatient returns success so the dashboard
   * table shows the new row without waiting for a Firestore round-trip. The
   * stub is built by buildOptimisticPatientSummary (a pure utility) and is
   * replaced by the real server-computed summary on the next SWR cycle (~30s).
   *
   * Guards against double-submit: if a patient with the same ID already
   * exists in the list (e.g. user double-clicked), the duplicate is skipped.
   */
  const optimisticAddPatient = useCallback((summaryStub) => {
    if (!summaryStub?.patientId) return;
    applyOptimisticPatientsUpdate((prev) => {
      if (prev.some((p) => p?.patientId === summaryStub.patientId)) return prev;
      return [summaryStub, ...prev];
    });
  }, [applyOptimisticPatientsUpdate]);

  /**
   * Optimistically patch fields on an existing patient in the in-memory list.
   *
   * Used after actions that mutate an existing patient's summary-visible fields
   * (e.g. clinician assignment) so the dashboard table reflects the change
   * immediately without waiting for patient_summary_trigger to propagate.
   *
   * The patch is a shallow merge onto the matching patient object. Nested
   * fields (like clinicians) should be passed as a complete replacement for
   * the nested object, not a deep merge — this matches how the server-computed
   * summary overwrites the entire clinicians map.
   *
   * The real server-computed summary replaces this on the next SWR cycle (~30s).
   */
  const optimisticUpdatePatient = useCallback((patientId, patch) => {
    if (!patientId || !patch) return;
    applyOptimisticPatientsUpdate((prev) => prev.map((p) => {
      if (p?.patientId !== patientId) return p;
      return { ...p, ...patch };
    }));
  }, [applyOptimisticPatientsUpdate]);

  const loadPatientsCacheFromSession = useCallback(() => {
    try {
      const raw = sessionStorage.getItem(PATIENTS_CACHE_STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || !Array.isArray(parsed.patients) || !parsed.meta) return null;
      return parsed;
    } catch (e) {
      console.warn('Unable to read patients cache from sessionStorage:', e);
      return null;
    }
  }, []);

  const clearPatientsCacheFromSession = useCallback(() => {
    try {
      sessionStorage.removeItem(PATIENTS_CACHE_STORAGE_KEY);
    } catch (e) {
      // Ignore
    }
  }, []);

  // Internal: decide whether to use cache or refresh, with SWR behavior
  const ensurePatientsWithCache = useCallback(async (currentUserId, clinicianData, { force = false } = {}) => {
    const role = clinicianData.userType;
    const org = clinicianData.organisation;
    const now = Date.now();
    let cacheMatches =
      patientsCacheMeta.loadedForUserId === currentUserId &&
      patientsCacheMeta.loadedForOrg === org &&
      patientsCacheMeta.loadedRole === role;
    let isFresh = cacheMatches && (now - patientsCacheMeta.lastLoadedAt) < PATIENTS_TTL_MS;

    // Attempt to hydrate from sessionStorage if needed (cold reloads)
    const firstLoad = patientsCacheMeta.lastLoadedAt === 0;
    if (!force && (firstLoad || !cacheMatches) && patientsData.length === 0) {
      const stored = loadPatientsCacheFromSession();
      if (stored && stored.meta && stored.patients) {
        const storedMeta = stored.meta;
        const matchesStoredKey =
          storedMeta.loadedForUserId === currentUserId &&
          storedMeta.loadedForOrg === org &&
          storedMeta.loadedRole === role;
        if (matchesStoredKey) {
          // Hydrate immediately for instant paint
          setPatientsData(stored.patients);
          setPatientsCacheMeta(storedMeta);
          setPatientsLoading(false);
          const ageMs = now - (storedMeta.lastLoadedAt || 0);
          const fresh = ageMs < PATIENTS_TTL_MS;
          // Update local flags to reflect hydrated state
          cacheMatches = true;
          isFresh = fresh;
          if (fresh) {
            console.log('Hydrated summaries from sessionStorage (fresh)');
            return;
          } else {
            console.log('Hydrated summaries from sessionStorage (stale) - scheduling background refresh');
            // Schedule SWR immediately and return without blocking
            if (!inFlightPatientsFetchRef.current) {
              inFlightPatientsFetchRef.current = (async () => {
                try {
                  const list = await fetchSummariesFromServer(clinicianData, currentUserId);
                  // Re-validate key context before committing
                  const stillSameUser = currentUser?.uid === currentUserId;
                  if (!stillSameUser) return;
                  setPatientsData(list);
                  const newMeta = {
                    lastLoadedAt: Date.now(),
                    loadedForOrg: org,
                    loadedForUserId: currentUserId,
                    loadedRole: role,
                  };
                  setPatientsCacheMeta(newMeta);
                  savePatientsCacheToSession(list, newMeta);
                  console.log(`Background refreshed summaries (${list.length}) after session hydration`);
                } catch (e) {
                  console.error('Background refresh failed (hydrated):', e);
                } finally {
                  inFlightPatientsFetchRef.current = null;
                }
              })();
            }
            return;
          }
        }
      }
    }
    // Always fetch if:
    // - force is true
    // - cache does not match current user/org/role
    // - there is no data in memory (e.g., after logout we clear patientsData but meta may still be set)
    if (force || !cacheMatches || patientsData.length === 0 || firstLoad) {
      // Show loading only when we don't already have hydrated data to render
      if (!cacheMatches || patientsData.length === 0 || firstLoad) {
        setPatientsLoading(true);
      }
      const list = await fetchSummariesFromServer(clinicianData, currentUserId);
      setPatientsData(list);
      setPatientsCacheMeta({
        lastLoadedAt: Date.now(),
        loadedForOrg: org,
        loadedForUserId: currentUserId,
        loadedRole: role,
      });
      // Persist to sessionStorage for fast reloads
      savePatientsCacheToSession(list, {
        lastLoadedAt: Date.now(),
        loadedForOrg: org,
        loadedForUserId: currentUserId,
        loadedRole: role,
      });
      console.log(`Loaded summaries (${list.length}) [force=${!!force} | cacheMatch=${cacheMatches} | firstLoad=${firstLoad}]`);
      setPatientsLoading(false);
      return;
    }

    if (isFresh) {
      // Cache is valid; no network call
      console.log('Using cached patients (fresh)');
      setPatientsLoading(false);
      return;
    }

    // Stale-while-revalidate: use cached data immediately, refresh in background once
    if (!inFlightPatientsFetchRef.current) {
      inFlightPatientsFetchRef.current = (async () => {
        try {
          const list = await fetchSummariesFromServer(clinicianData, currentUserId);
          // Re-validate key context before committing
          const stillSameUser = currentUser?.uid === currentUserId;
          if (!stillSameUser) return;
          setPatientsData(list);
          const newMeta = {
            lastLoadedAt: Date.now(),
            loadedForOrg: org,
            loadedForUserId: currentUserId,
            loadedRole: role,
          };
          setPatientsCacheMeta(newMeta);
          // Persist refreshed list/meta
          savePatientsCacheToSession(list, newMeta);
          console.log(`Background refreshed summaries (${list.length})`);
        } catch (e) {
          console.error('Background refresh failed:', e);
        } finally {
          inFlightPatientsFetchRef.current = null;
        }
      })();
    }
  }, [PATIENTS_TTL_MS, currentUser, patientsCacheMeta, patientsData.length, fetchSummariesFromServer, loadPatientsCacheFromSession, savePatientsCacheToSession]);

  // Add a specific function to refresh only the patients data (respect TTL by default)
  const refreshPatientsData = useCallback(async (opts = { force: false }) => {
    try {
      if (!currentUser) return;
      const clinicianDoc = await getDoc(doc(db, 'clinicians', currentUser.uid));
      if (!clinicianDoc.exists()) return;
      const clinicianData = clinicianDoc.data();
      await ensurePatientsWithCache(currentUser.uid, clinicianData, { force: !!opts.force });
    } catch (error) {
      console.error("Error refreshing patients data:", error);
      setPatientsLoading(false);
    }
  }, [currentUser, ensurePatientsWithCache]);

  const fetchUserData = useCallback(async () => {
    try {
      if (!currentUser || !claims) {
        console.warn("No authenticated user or claims found.");
        return;
      }

      // RMS users store their data in different Firestore collections
      // (e.g. organisations/{orgId}/patientRecords/... for service users, and
      // a separate RMS clinicians structure for providers). The queries below
      // target the Assessment collections (`clinicians`, `patients`), which
      // RMS users don't have security-rule access to. Attempting them would
      // produce noisy "Missing or insufficient permissions" console errors.
      const isRmsUser =
        claims.accountType === 'RMS Provider User' ||
        claims.accountType === 'RMS Service User';

      if (isRmsUser) {
        // RMS pages fetch their own data via dedicated RMS hooks/contexts,
        // so there's nothing for GlobalStateContext to load here.
        return;
      }
      
      console.log("Fetching user data for:", currentUser.uid);

      // Use claims from useAuth hook instead of fetching again
      const accountType = claims.accountType;

              if (accountType === 'Provider') {
        // This is a provider, check clinicians collection
        const clinicianDoc = await getDoc(doc(db, 'clinicians', currentUser.uid));

        if (clinicianDoc.exists()) {
          console.log("User found in clinicians collection");
          const clinicianData = clinicianDoc.data();
          setUserData({
            ...clinicianData
          });
          localStorage.setItem('userData', JSON.stringify(clinicianData));

          // Patients list with TTL cache + SWR
          await ensurePatientsWithCache(currentUser.uid, clinicianData, { force: false });

          // If user is Org. Admin or Support, fetch all providers in their organisation
          // so both roles can manage staff accounts (view, copy ID, delete) in the
          // Staff Management dashboard.
          if (clinicianData.userType === 'Org. Admin' || clinicianData.userType === 'Support') {
            const providerAccountsQuery = query(
              collection(db, 'clinicians'),
              where('organisation', '==', clinicianData.organisation)
            );
            const providerAccountsSnapshot = await getDocs(providerAccountsQuery);
            const providerAccountsList = providerAccountsSnapshot.docs.map(doc => ({
              clinicianId: doc.id,
              ...doc.data()
            }));
            setProviderAccountsData(providerAccountsList);
          }

          return;
        }

      } else if (accountType === 'Service User') {
        // Modular informants (Adult Autism, CYP ADHD, etc.) live exclusively in
        // informants/{uid} — route there directly based on claims to avoid a
        // wasted patients/ read.
        const isModularInformant = claims.userType === 'Informant'
          && claims.assessmentModule
          && claims.assessmentModule !== 'adultAdhd';
        if (isModularInformant) {
          const informantDocRef = doc(db, 'informants', currentUser.uid);
          const informantDoc = await getDoc(informantDocRef);
          if (informantDoc.exists()) {
            console.log("User found in informants collection (Adult Autism informant)");
            const informantData = informantDoc.data();
            setUserData(informantData);
            localStorage.setItem('userData', JSON.stringify(informantData));
            return;
          }
        } else {
          // ADHD patients/informants and Autism patients all live in patients/
          const patientDocRef = doc(db, 'patients', currentUser.uid);
          const patientDoc = await getDoc(patientDocRef);

          if (patientDoc.exists()) {
            console.log("User found in patients collection");
            const patientData = patientDoc.data();
            setUserData(patientData);
            localStorage.setItem('userData', JSON.stringify(patientData));
            return;
          }
        }
      }

      // If we get here, user wasn't found in the expected collection
      console.warn(`User not found in expected collection with ID: ${currentUser.uid}`);

    } catch (error) {
      console.error("Error in fetchUserData: ", error);
    }
  }, [currentUser, claims, ensurePatientsWithCache]);

  // Maintain a stable reference to fetchUserData for effects
  const fetchUserDataRef = React.useRef(fetchUserData);
  useEffect(() => {
    fetchUserDataRef.current = fetchUserData;
  }, [fetchUserData]);

  // Provide a stable external refresh function that proxies to the latest impl.
  // Design decision: Consumers like ClinicianDashboard should not re-run their
  // init effects just because this function's identity changes when internal
  // state (e.g., patients cache) updates. Using a stable wrapper eliminates
  // render loops while still calling the current fetch logic via the ref.
  const refreshUserDataStable = React.useCallback(async () => {
    if (fetchUserDataRef.current) {
      await fetchUserDataRef.current();
    }
  }, []);

  // Refresh ONLY the clinician/user document (userData) without triggering a
  // patient summaries refetch. Used after actions that update the clinician doc
  // (e.g. setupNewPatient adds to clinicians/{uid}.patients) but where an
  // optimistic patient entry has just been injected into patientsData.
  //
  // Calling the full refreshUserData (fetchUserData) here would cascade into
  // ensurePatientsWithCache, which — if the cache is stale — fires a background
  // getDocs that can overwrite the optimistic stub with stale server data before
  // the Firestore trigger has propagated.
  //
  // Mirrors the Provider branch of fetchUserData (lines ~451-482) but deliberately
  // skips ensurePatientsWithCache and providerAccountsData refresh.
  const refreshUserDataOnly = useCallback(async () => {
    try {
      if (!currentUser || !claims) return;
      const isRmsUser =
        claims.accountType === 'RMS Provider User' ||
        claims.accountType === 'RMS Service User';
      if (isRmsUser) return;

      const accountType = claims.accountType;
      if (accountType === 'Provider') {
        const clinicianDoc = await getDoc(doc(db, 'clinicians', currentUser.uid));
        if (clinicianDoc.exists()) {
          const clinicianData = clinicianDoc.data();
          setUserData({ ...clinicianData });
          localStorage.setItem('userData', JSON.stringify(clinicianData));
        }
      }
    } catch (error) {
      console.error("Error in refreshUserDataOnly:", error);
    }
  }, [currentUser, claims]);

  // Note: We previously used a window event ("td:patients-refresh") as a simple cross-component
  // refresh mechanism. Now that dialogs can call refreshPatientsData directly, we avoid the
  // global event bus for better maintainability and debuggability.

  // Stable signature of the security-relevant custom claims. We depend on this
  // (rather than the whole `claims` object, whose identity changes on every
  // token refresh) so the effect below re-runs ONLY when a meaningful role
  // change lands — e.g. an admin demotes this user mid-session and
  // useAccountStatusMonitor force-refreshes the token. Without this, `userData`
  // (which drives nav/role-gated UI) would stay stale on the demoted user's
  // screen until they next reloaded, leaving elevated controls visible.
  const claimsSignature = `${claims?.accountType || ''}|${claims?.userType || ''}|${claims?.isAssignableClinician ?? ''}`;

  // React to auth state changes from useAuth hook
  useEffect(() => {
    // While auth is initializing, do nothing.
    // Design decision: This prevents accidental cache wipes on regular browser refresh.
    if (authLoading) return;

    if (isAuthenticated && currentUser) {
      console.log("Auth state changed, user is:", currentUser.uid);
      fetchUserDataRef.current();
      return;
    }

      console.log("User is not authenticated, clearing data");
      setUserData(null);
      setPatientsData([]);
      setProviderAccountsData([]);
      localStorage.removeItem('userData');
    // Reset patients cache meta on logout so next login triggers a fresh load
    setPatientsCacheMeta({
      lastLoadedAt: 0,
      loadedForOrg: null,
      loadedForUserId: null,
      loadedRole: null,
    });
    setPatientsLoading(false);
    clearPatientsCacheFromSession();
    // Clear the dashboard's persisted view config (filters can contain a patient
    // name) so the next clinician on this tab starts from a clean default view.
    // This branch also runs on token expiry, covering the shared-machine case.
    clearDashboardViewState();
    // Dual-model comparison drafts are clinical paragraph text (PII) held
    // only in this tab's sessionStorage. Wipe them on the same de-auth
    // branch so a later clinician never sees the previous patient's drafts.
    clearCompareOutputsCache();
    // Adult Autism History persists per-subsection clinician notes (PII) and
    // navigation state in sessionStorage; wipe them on the same de-auth branch
    // so a different clinician on a shared tab never inherits them.
    clearAutismHistorySessionState();
    // The modular (subcollection-based) pathways cache patient/informant data in
    // localStorage for instant cold-load paint. The ADHD contexts clear their
    // equivalent cache on sign-out; this brings the modular pathways to parity so
    // an autism patient's cached data does not linger at rest after logout.
    clearModularPatientLocalStorage();
  }, [authLoading, isAuthenticated, currentUser, claimsSignature, clearPatientsCacheFromSession]);

  return (
    <GlobalStateContext.Provider
      value={{
        userData,
        patientsData,
        providerAccountsData,
        patientsLoading,
        setUserData,
        refreshUserData: refreshUserDataStable,
        refreshUserDataOnly,
        refreshPatientsData,  
        refreshProviderAccountsData,
        optimisticRemovePatient,
        optimisticArchivePatient,
        optimisticRestorePatient,
        optimisticAddPatient,
        optimisticUpdatePatient,
      }}
    >
      {children}
    </GlobalStateContext.Provider>
  );
};